import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, getImageUrl } from '../config/api';

const AdminAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState({
    totalStudents: 0,
    totalClasses: 0,
    totalSubjects: 0,
    totalLessons: 0,
    totalExams: 0,
    attendanceRate: 0,
    feeCollectionRate: 0,
    averagePerformance: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [detailedStats, setDetailedStats] = useState({
    classDistribution: [],
    subjectPerformance: [],
    attendanceTrends: [],
    feeCollectionTrends: []
  });

  useEffect(() => {
    loadAnalyticsData();
  }, [selectedPeriod]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);

      // Fetch all data sources
      const [
        studentsRes,
        classesRes,
        subjectsRes,
        lessonsRes,
        midtermExamsRes,
        finalExamsRes,
        attendanceRes,
        paymentsRes,
        progressRes
      ] = await Promise.all([
        fetch(API_ENDPOINTS.STUDENTS),
        fetch(API_ENDPOINTS.CLASSES),
        fetch(API_ENDPOINTS.SUBJECTS),
        fetch(API_ENDPOINTS.LESSONS),
        fetch(`${API_ENDPOINTS.EXAMS}/Midterm`),
        fetch(`${API_ENDPOINTS.EXAMS}/Final`),
        fetch(API_ENDPOINTS.STUDENTS).then(() => getAllAttendance()),
        fetch(API_ENDPOINTS.STUDENTS).then(() => getAllPayments()),
        fetch(API_ENDPOINTS.STUDENTS).then(() => getAllProgress())
      ]);

      const students = await studentsRes.json();
      const classes = await classesRes.json();
      const subjects = await subjectsRes.json();
      const lessons = await lessonsRes.json();
      const midtermExams = await midtermExamsRes.json();
      const finalExams = await finalExamsRes.json();
      const attendance = await attendanceRes;
      const payments = await paymentsRes;
      const progress = await progressRes;

      // Calculate analytics
      const totalExams = midtermExams.length + finalExams.length;
      const attendanceRate = calculateAttendanceRate(attendance);
      const feeCollectionRate = calculateFeeCollectionRate(payments);
      const averagePerformance = calculateAveragePerformance(progress);

      // Class distribution
      const classDistribution = classes.map(cls => ({
        name: cls,
        count: students.filter(s => s.className === cls).length
      }));

      // Subject performance
      const subjectPerformance = calculateSubjectPerformance(progress);

      setAnalytics({
        totalStudents: students.length,
        totalClasses: classes.length,
        totalSubjects: subjects.length,
        totalLessons: lessons.length,
        totalExams,
        attendanceRate,
        feeCollectionRate,
        averagePerformance
      });

      setDetailedStats({
        classDistribution,
        subjectPerformance,
        attendanceTrends: generateAttendanceTrends(attendance),
        feeCollectionTrends: generateFeeCollectionTrends(payments)
      });

    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAllAttendance = async () => {
    const students = await fetch(API_ENDPOINTS.STUDENTS).then(r => r.json());
    const allAttendance = [];

    for (const student of students) {
      try {
        const attendance = await fetch(`${API_ENDPOINTS.ATTENDANCE}/${student.username}`)
          .then(r => r.json());
        allAttendance.push(...attendance);
      } catch (error) {
        console.error(`Error fetching attendance for ${student.username}:`, error);
      }
    }

    return allAttendance;
  };

  const getAllPayments = async () => {
    const students = await fetch(API_ENDPOINTS.STUDENTS).then(r => r.json());
    const allPayments = [];

    for (const student of students) {
      try {
        const payments = await fetch(`${API_ENDPOINTS.PAYMENTS || API_ENDPOINTS.FEES}/${student.username}`)
          .then(r => r.json());
        allPayments.push(...payments);
      } catch (error) {
        console.error(`Error fetching payments for ${student.username}:`, error);
      }
    }

    return allPayments;
  };

  const getAllProgress = async () => {
    const students = await fetch(API_ENDPOINTS.STUDENTS).then(r => r.json());
    const allProgress = [];

    for (const student of students) {
      try {
        const progress = await fetch(`${API_ENDPOINTS.PROGRESS}/${student.username}`)
          .then(r => r.json());
        allProgress.push(...progress);
      } catch (error) {
        console.error(`Error fetching progress for ${student.username}:`, error);
      }
    }

    return allProgress;
  };

  const calculateAttendanceRate = (attendance) => {
    if (attendance.length === 0) return 0;
    const presentCount = attendance.filter(a => a.status === 'present').length;
    return Math.round((presentCount / attendance.length) * 100);
  };

  const calculateFeeCollectionRate = (payments) => {
    if (payments.length === 0) return 0;
    const completedPayments = payments.filter(p => p.status === 'completed').length;
    return Math.round((completedPayments / payments.length) * 100);
  };

  const calculateAveragePerformance = (progress) => {
    if (progress.length === 0) return 0;
    const totalScore = progress.reduce((sum, p) => sum + (p.score || 0), 0);
    return Math.round((totalScore / progress.length) * 100) / 100;
  };

  const calculateSubjectPerformance = (progress) => {
    const subjectStats = {};

    progress.forEach(item => {
      if (!subjectStats[item.subject]) {
        subjectStats[item.subject] = {
          subject: item.subject,
          totalScores: 0,
          count: 0,
          completedCount: 0
        };
      }

      subjectStats[item.subject].totalScores += item.score || 0;
      subjectStats[item.subject].count++;

      if (item.completed) {
        subjectStats[item.subject].completedCount++;
      }
    });

    return Object.values(subjectStats).map(stats => ({
      subject: stats.subject,
      averageScore: Math.round((stats.totalScores / stats.count) * 100) / 100,
      completionRate: Math.round((stats.completedCount / stats.count) * 100)
    })).sort((a, b) => b.averageScore - a.averageScore);
  };

  const generateAttendanceTrends = (attendance) => {
    // Generate last 7 days attendance trend
    const trends = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      const dayAttendance = attendance.filter(a => a.date === dateStr);
      const presentCount = dayAttendance.filter(a => a.status === 'present').length;
      const totalCount = dayAttendance.length;
      const rate = totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 0;

      trends.push({
        date: dateStr,
        rate,
        present: presentCount,
        total: totalCount
      });
    }

    return trends;
  };

  const generateFeeCollectionTrends = (payments) => {
    // Generate last 7 days payment trend
    const trends = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      const dayPayments = payments.filter(p => p.date.startsWith(dateStr));
      const completedCount = dayPayments.filter(p => p.status === 'completed').length;
      const totalCount = dayPayments.length;
      const rate = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

      trends.push({
        date: dateStr,
        rate,
        completed: completedCount,
        total: totalCount
      });
    }

    return trends;
  };

  const exportAnalyticsReport = () => {
    const reportData = {
      generatedAt: new Date().toISOString(),
      period: selectedPeriod,
      summary: analytics,
      classDistribution: detailedStats.classDistribution,
      subjectPerformance: detailedStats.subjectPerformance,
      attendanceTrends: detailedStats.attendanceTrends,
      feeCollectionTrends: detailedStats.feeCollectionTrends
    };

    const csvData = [
      ['Metric', 'Value'],
      ['Total Students', analytics.totalStudents],
      ['Total Classes', analytics.totalClasses],
      ['Total Subjects', analytics.totalSubjects],
      ['Total Lessons', analytics.totalLessons],
      ['Total Exams', analytics.totalExams],
      ['Attendance Rate (%)', analytics.attendanceRate],
      ['Fee Collection Rate (%)', analytics.feeCollectionRate],
      ['Average Performance', analytics.averagePerformance]
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `school-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading analytics dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">School Analytics Dashboard</h1>
          <div className="flex space-x-4">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
              <option value="quarter">Last Quarter</option>
              <option value="year">Last Year</option>
            </select>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">Students</div>
            <h3 className="text-lg font-semibold text-gray-800">{analytics.totalStudents}</h3>
            <p className="text-sm text-gray-600">Total Students</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">Books</div>
            <h3 className="text-lg font-semibold text-gray-800">{analytics.attendanceRate}%</h3>
            <p className="text-sm text-gray-600">Attendance Rate</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">💰</div>
            <h3 className="text-lg font-semibold text-gray-800">{analytics.feeCollectionRate}%</h3>
            <p className="text-sm text-gray-600">Fee Collection</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">Goals</div>
            <h3 className="text-lg font-semibold text-gray-800">{analytics.averagePerformance}</h3>
            <p className="text-sm text-gray-600">Avg Performance</p>
          </div>
        </div>

        {/* Content Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-blue-50 rounded-lg shadow p-6 text-center">
            <div className="text-2xl mb-2">School</div>
            <h3 className="text-lg font-semibold text-blue-800">{analytics.totalClasses}</h3>
            <p className="text-sm text-blue-600">Classes</p>
          </div>

          <div className="bg-green-50 rounded-lg shadow p-6 text-center">
            <div className="text-2xl mb-2">Study</div>
            <h3 className="text-lg font-semibold text-green-800">{analytics.totalSubjects}</h3>
            <p className="text-sm text-green-600">Subjects</p>
          </div>

          <div className="bg-yellow-50 rounded-lg shadow p-6 text-center">
            <div className="text-2xl mb-2">Note</div>
            <h3 className="text-lg font-semibold text-yellow-800">{analytics.totalLessons}</h3>
            <p className="text-sm text-yellow-600">Lessons</p>
          </div>

          <div className="bg-purple-50 rounded-lg shadow p-6 text-center">
            <div className="text-2xl mb-2">Stats</div>
            <h3 className="text-lg font-semibold text-purple-800">{analytics.totalExams}</h3>
            <p className="text-sm text-purple-600">Exams</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Class Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Class Distribution</h2>
            <div className="space-y-3">
              {detailedStats.classDistribution.map((cls) => (
                <div key={cls.name} className="flex justify-between items-center">
                  <span className="text-gray-700">Class {cls.name}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(cls.count / analytics.totalStudents) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-800 w-8 text-right">
                      {cls.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Subject Performance */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Top Performing Subjects</h2>
            <div className="space-y-3">
              {detailedStats.subjectPerformance.slice(0, 5).map((subject) => (
                <div key={subject.subject} className="flex justify-between items-center">
                  <span className="text-gray-700">{subject.subject}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{ width: `${(subject.averageScore / 100) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-800 w-12 text-right">
                      {subject.averageScore}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Trends */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Attendance Trends (Last 7 Days)</h2>
            <div className="space-y-2">
              {detailedStats.attendanceTrends.map((day, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${day.rate}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-800 w-10 text-right">
                      {day.rate}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Payment Collection Trends</h2>
            <div className="space-y-2">
              {detailedStats.feeCollectionTrends.map((day, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{ width: `${day.rate}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-800 w-10 text-right">
                      {day.rate}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Export Options */}
        <div className="mt-8 bg-gray-50 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Export Analytics Report</h2>
          <div className="flex space-x-4">
            <button
              onClick={exportAnalyticsReport}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
            >
              Export Summary Report (CSV)
            </button>
            <button
              onClick={() => alert('Detailed PDF report would be implemented with a PDF generation library')}
              className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
            >
              Generate Detailed Report (PDF)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsDashboard;



